import React, { type ReactElement, type ReactNode, useContext } from 'react';

import Image, { type ImageProps } from 'next/image';

import classNames from 'classnames';

import Price from '@/common/components/price/price.component';
import Text from '@/common/components/text/text.component';

interface OrderProductProps {
  children: ReactNode;
  className?: string;
  disable?: boolean;
  onClick?: () => void;
}

interface ContainerProps {
  children: ReactNode;
  className?: string;
  type?: string;
  onClick?: () => void;
}

interface ProductPriceProps {
  price: number;
  discount?: number;
  className?: string;
  priceClassName?: string;
  discountClassName?: string;
}

interface ContainerType {
  default: string;
  parent: string;
  col: string;
  row: string;
}

const containerTypes: ContainerType = {
  default: 'flex items-center justify-center',
  parent: 'flex h-full w-full flex-row items-start gap-y-2  py-0.5 ',
  col: 'flex flex-col',
  row: 'flex w-full justify-between ',
};

const OrderProductContext = React.createContext<{ disable?: boolean }>({
  disable: false,
});

const disableClassName = 'opacity-50';

export default function OrderProduct({
  children,
  className,
  onClick,
  disable,
}: OrderProductProps): ReactElement {
  return (
    <OrderProductContext.Provider value={{ disable }}>
      <div
        className={classNames('flex w-full items-center gap-x-3', className)}
        onClick={onClick}
      >
        {children}
      </div>
    </OrderProductContext.Provider>
  );
}

function Container({
  children,
  className,
  type = 'default',
  onClick,
}: ContainerProps) {
  return (
    <div
      className={classNames(
        containerTypes[type as keyof ContainerType],
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

function ProductImage({
  className,
  width = 80,
  height = 80,
  onClick,
  ...props
}: ImageProps) {
  const { disable } = useContext(OrderProductContext);
  return (
    <Image
      width={width}
      height={height}
      className={classNames('rounded-md', className, [
        disable && disableClassName,
      ])}
      {...props}
      onClick={onClick}
    />
  );
}

function Title({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const { disable } = useContext(OrderProductContext);
  return (
    <div
      className={classNames('h-auto', [disable && disableClassName], className)}
      onClick={onClick}
    >
      <Text className="  h-auto w-[100px] truncate text-sm font-semibold text-black-default  lg:line-clamp-1 lg:w-fit">
        {children}
      </Text>
    </div>
  );
}

function Properties({ children, className }: OrderProductProps) {
  const { disable } = useContext(OrderProductContext);
  return (
    <div
      className={classNames(
        'flex w-full items-center justify-start gap-x-1',
        className,
        [disable && 'opacity-50']
      )}
    >
      {children}
    </div>
  );
}

function ProductPrice({
  priceClassName,
  discountClassName,
  price,
  discount = 0,
}: ProductPriceProps) {
  const { disable } = useContext(OrderProductContext);

  return (
    <Price
      priceClassName={classNames(priceClassName, [disable && disableClassName])}
      discountClassName={discountClassName}
      currencyClassName="!font-normal !text-sm text-secondary-300"
      price={price}
      discount={discount}
    />
  );
}

OrderProduct.Container = Container;
OrderProduct.ProductImage = ProductImage;
OrderProduct.Title = Title;
OrderProduct.Properties = Properties;
OrderProduct.ProductPrice = ProductPrice;
